import React, { useState, useEffect } from 'react';
import { X, CheckSquare, Square, Plus, Trash2, ChevronDown, ChevronUp, Code, AlertCircle, Zap, Clock, Target, Cloud, CloudOff } from 'lucide-react';
import { API_CONFIG } from '../config/api';

interface Todo {
  id: string;
  text: string;
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
  category: 'bug' | 'feature' | 'optimization' | 'documentation' | 'deployment';
  createdAt: string;
  completedAt?: string;
}

export const DevTodos: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [todos, setTodos] = useState<Todo[]>([]);
  const [newTodo, setNewTodo] = useState('');
  const [selectedPriority, setSelectedPriority] = useState<Todo['priority']>('medium');
  const [selectedCategory, setSelectedCategory] = useState<Todo['category']>('feature');
  const [filter, setFilter] = useState<'all' | 'active' | 'completed'>('all');
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  
  // Check if we're in dev mode (you can also check for specific IP, user agent, etc.)
  const isDev = window.location.hostname === 'localhost' || 
                window.location.hostname === '127.0.0.1' ||
                window.location.search.includes('dev=true') ||
                localStorage.getItem('devMode') === 'true';

  // Use API URL from config
  const getApiUrl = () => {
    return API_CONFIG.API_URL.replace('/api', '/api/todos');
  };

  // Load todos from API
  const loadTodos = async () => {
    try {
      const response = await fetch(getApiUrl());
      if (response.ok) {
        const data = await response.json();
        if (data.todos && data.todos.length > 0) {
          setTodos(data.todos);
        } else {
          // Fallback to localStorage if API is empty
          const savedTodos = localStorage.getItem('dev-todos');
          if (savedTodos) {
            const localTodos = JSON.parse(savedTodos);
            setTodos(localTodos);
            // Save to API
            saveTodosToAPI(localTodos);
          }
        }
      }
    } catch (error) {
      console.error('Failed to load todos from API:', error);
      // Fallback to localStorage
      const savedTodos = localStorage.getItem('dev-todos');
      if (savedTodos) {
        setTodos(JSON.parse(savedTodos));
      }
    }
  };

  // Save todos to API
  const saveTodosToAPI = async (todosToSave: Todo[]) => {
    setIsSaving(true);
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ todos: todosToSave })
      });
      
      if (response.ok) {
        setLastSaved(new Date());
        // Also save to localStorage as backup
        localStorage.setItem('dev-todos', JSON.stringify(todosToSave));
      }
    } catch (error) {
      console.error('Failed to save todos to API:', error);
      // Still save to localStorage as fallback
      localStorage.setItem('dev-todos', JSON.stringify(todosToSave));
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    // Load todos from API or localStorage
    if (isDev) {
      loadTodos();
    }
    
    // Automatically show panel if ?dev=true is in URL
    if (window.location.search.includes('dev=true')) {
      setIsVisible(true);
      localStorage.setItem('dev-panel-visible', 'true');
    } else {
      // Check if dev panel should be visible from localStorage
      const devPanelVisible = localStorage.getItem('dev-panel-visible');
      if (devPanelVisible === 'true' && isDev) {
        setIsVisible(true);
      }
    }
    
  }, [isDev]);
  
  useEffect(() => {
    // Save todos to API and localStorage whenever they change
    if (todos.length > 0 || localStorage.getItem('dev-todos')) {
      saveTodosToAPI(todos);
    }
  }, [todos]);
  
  const addTodo = () => {
    if (newTodo.trim()) {
      const todo: Todo = {
        id: Date.now().toString(),
        text: newTodo,
        completed: false,
        priority: selectedPriority,
        category: selectedCategory,
        createdAt: new Date().toISOString()
      };
      setTodos([todo, ...todos]);
      setNewTodo('');
    }
  };
  
  const toggleTodo = (id: string) => {
    setTodos(todos.map(todo => 
      todo.id === id 
        ? { 
            ...todo, 
            completed: !todo.completed,
            completedAt: !todo.completed ? new Date().toISOString() : undefined
          }
        : todo
    ));
  };
  
  const deleteTodo = (id: string) => {
    setTodos(todos.filter(todo => todo.id !== id));
  };
  
  const filteredTodos = todos.filter(todo => {
    if (filter === 'active') return !todo.completed;
    if (filter === 'completed') return todo.completed;
    return true;
  });
  
  const stats = {
    total: todos.length,
    completed: todos.filter(t => t.completed).length,
    high: todos.filter(t => !t.completed && t.priority === 'high').length,
    bugs: todos.filter(t => !t.completed && t.category === 'bug').length
  };
  
  const getPriorityColor = (priority: Todo['priority']) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50 border-red-200';
      case 'medium': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'low': return 'text-green-600 bg-green-50 border-green-200';
    }
  };
  
  const getCategoryIcon = (category: Todo['category']) => {
    switch (category) {
      case 'bug': return <AlertCircle className="w-4 h-4" />;
      case 'feature': return <Zap className="w-4 h-4" />;
      case 'optimization': return <Clock className="w-4 h-4" />;
      case 'documentation': return <Code className="w-4 h-4" />;
      case 'deployment': return <Target className="w-4 h-4" />;
    }
  };
  
  if (!isDev || !isVisible) return null;
  
  return (
    <div className="fixed bottom-4 right-4 z-[9999] max-w-md">
      {/* Main Panel */}
      <div className="bg-white rounded-2xl shadow-2xl border-4 border-slate-900 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Code className="w-5 h-5" />
              <h3 className="font-black text-lg">Dev TODOs</h3>
              <span className="text-xs bg-white/20 px-2 py-1 rounded-full">
                {stats.completed}/{stats.total}
              </span>
              {isSaving && (
                <span className="text-xs bg-yellow-500/20 px-2 py-1 rounded-full flex items-center gap-1">
                  <Cloud className="w-3 h-3 animate-pulse" />
                  Saving...
                </span>
              )}
              {!isSaving && lastSaved && (
                <span className="text-xs bg-green-500/20 px-2 py-1 rounded-full flex items-center gap-1">
                  <Cloud className="w-3 h-3" />
                  Saved
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                {isMinimized ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              <button
                onClick={() => {
                  setIsVisible(false);
                  localStorage.setItem('dev-panel-visible', 'false');
                }}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          {/* Stats Bar */}
          {!isMinimized && (
            <div className="flex gap-4 mt-3 text-xs">
              {stats.high > 0 && (
                <span className="bg-red-500/20 px-2 py-1 rounded">
                  🔥 {stats.high} High
                </span>
              )}
              {stats.bugs > 0 && (
                <span className="bg-orange-500/20 px-2 py-1 rounded">
                  🐛 {stats.bugs} Bugs
                </span>
              )}
              <span className="bg-white/10 px-2 py-1 rounded">
                ✅ {Math.round((stats.completed / stats.total) * 100) || 0}% Done
              </span>
            </div>
          )}
        </div>
        
        {!isMinimized && (
          <>
            {/* Add Todo Form */}
            <div className="p-4 border-b-2 border-gray-200">
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTodo}
                  onChange={(e) => setNewTodo(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTodo()}
                  placeholder="Add a new TODO..."
                  className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-slate-900 focus:outline-none"
                />
                <button
                  onClick={addTodo}
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-bold hover:from-blue-600 hover:to-purple-600 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
              
              <div className="flex gap-2">
                <select
                  value={selectedPriority}
                  onChange={(e) => setSelectedPriority(e.target.value as Todo['priority'])}
                  className="px-2 py-1 text-sm border-2 border-gray-300 rounded focus:border-slate-900 focus:outline-none"
                >
                  <option value="high">🔴 High</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="low">🟢 Low</option>
                </select>
                
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value as Todo['category'])}
                  className="px-2 py-1 text-sm border-2 border-gray-300 rounded focus:border-slate-900 focus:outline-none"
                >
                  <option value="bug">🐛 Bug</option>
                  <option value="feature">⚡ Feature</option>
                  <option value="optimization">⚙️ Optimization</option>
                  <option value="documentation">📝 Docs</option>
                  <option value="deployment">🚀 Deploy</option>
                </select>
              </div>
            </div>
            
            {/* Filter Tabs */}
            <div className="flex border-b-2 border-gray-200">
              {(['all', 'active', 'completed'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`flex-1 px-4 py-2 text-sm font-semibold capitalize transition-colors ${
                    filter === f 
                      ? 'bg-slate-900 text-white' 
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
            
            {/* Todo List */}
            <div className="max-h-96 overflow-y-auto">
              {filteredTodos.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Target className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No TODOs yet!</p>
                  <p className="text-xs mt-1">Add one above to get started</p>
                </div>
              ) : (
                <div className="p-2">
                  {filteredTodos.map(todo => (
                    <div
                      key={todo.id}
                      className={`mb-2 p-3 rounded-lg border-2 transition-all ${
                        todo.completed 
                          ? 'bg-gray-50 border-gray-200 opacity-60' 
                          : `${getPriorityColor(todo.priority)}`
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <button
                          onClick={() => toggleTodo(todo.id)}
                          className="mt-0.5"
                        >
                          {todo.completed ? (
                            <CheckSquare className="w-5 h-5 text-green-600" />
                          ) : (
                            <Square className="w-5 h-5" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <p className={`${todo.completed ? 'line-through text-gray-500' : ''}`}>
                            {todo.text}
                          </p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="flex items-center gap-1 text-xs text-gray-600">
                              {getCategoryIcon(todo.category)}
                              {todo.category}
                            </span>
                            <span className="text-xs text-gray-400">
                              {new Date(todo.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => deleteTodo(todo.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
      
    </div>
  );
};